import { Location, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  Injector,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
  afterNextRender,
  computed,
  inject,
  signal,
} from '@angular/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatCard } from '@angular/material/card';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatDatepicker, MatDatepickerInput } from '@angular/material/datepicker';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { MatTooltip } from '@angular/material/tooltip';
import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { FormsModule } from '@angular/forms';
import {
  collection,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  Unsubscribe,
  where,
} from 'firebase/firestore';
import moment from 'moment';
import { dataStore, db } from '../../../app.component';
import { OverflowTooltipDirective } from '../../../directives/overflow-tooltip.directive';
import { Role } from '../../../enums';
import { valueToDate } from '../../../globals';
import { Daypart, DaypartDay, User } from '../../../interfaces';

interface OverviewColumn {
  key: string;
  date: Date;
  dateKey: string;
  daypart: Daypart;
}

interface OverviewColumnGroup {
  date: Date;
  dateKey: string;
  columns: OverviewColumn[];
}

interface OverviewCell {
  state: 'available' | 'scheduled' | 'exception' | 'unavailable';
  indicator?: string;
  ariaLabel: string;
}

@Component({
  selector: 'app-availability-overview',
  templateUrl: './availability-overview.component.html',
  styleUrls: ['./availability-overview.component.scss'],
  imports: [
    MatCard,
    MatButton,
    MatDatepicker,
    MatDatepickerInput,
    MatFormField,
    FormsModule,
    MatIcon,
    MatIconButton,
    MatInput,
    MatLabel,
    MatSuffix,
    MatProgressSpinner,
    MatTab,
    MatTabGroup,
    MatTooltip,
    NgTemplateOutlet,
    ScrollingModule,
    OverflowTooltipDirective,
  ],
})
export class AvailabilityOverviewComponent implements OnInit, OnDestroy {
  @ViewChildren(CdkVirtualScrollViewport) viewports!: QueryList<CdkVirtualScrollViewport>;
  readonly injector = inject(Injector);
  readonly Role = Role;

  readonly selectedIndex = signal(0);
  readonly usesPlannerPlanning = signal(dataStore.generalSettings?.usesPlannerPlanning ?? false);
  readonly coDriverEnabled = signal(dataStore.generalSettings?.coDriverEnabled ?? false);
  readonly searchTerm = signal('');
  readonly tabTransitioning = signal(false);
  readonly selectedWeekStart = signal(moment().startOf('isoWeek').toDate());
  readonly loading = signal(true);
  readonly assignmentRefreshing = signal(true);
  readonly error = signal(false);

  readonly users = signal<(User & { id: string })[]>([]);
  readonly dayparts = signal<Daypart[]>([]);
  readonly daypartDays = signal<DaypartDay[]>([]);

  readonly weekDates = computed(() => {
    const start = this.selectedWeekStart();
    return Array.from({ length: 7 }, (_, index) => moment(start).add(index, 'days').toDate());
  });

  readonly organisationId = dataStore.selectedOrganisation.id;
  usersUnsubscribe?: Unsubscribe;
  daypartsUnsubscribe?: Unsubscribe;
  daypartDaysUnsubscribe?: Unsubscribe;
  private initialSnapshots = {
    users: false,
    dayparts: false,
    daypartDays: false,
  };
  private assignmentCache = new Map<string, DaypartDay[]>();
  private daypartDaysRequestId = 0;

  readonly columns = computed(() => {
    const types = this.selectedIndex() === 0 ? ['driver'] : ['planner'];
    const selectedDayparts = this.dayparts()
      .filter((daypart) => types.includes(daypart.type ?? ''))
      .sort((left, right) => this.compareDayparts(left, right));

    return this.weekDates().flatMap((date) =>
      selectedDayparts
        .filter((daypart) => daypart.daypart.day === moment(date).isoWeekday() - 1)
        .map((daypart) => ({
          key: `${this.dateKey(date)}-${daypart.id}`,
          date,
          dateKey: this.dateKey(date),
          daypart,
        })),
    );
  });

  readonly columnGroups = computed<OverviewColumnGroup[]>(() => {
    return this.columns().reduce<OverviewColumnGroup[]>((groups, column) => {
      const currentGroup = groups[groups.length - 1];
      if (currentGroup?.dateKey === column.dateKey) {
        currentGroup.columns.push(column);
      } else {
        groups.push({ date: column.date, dateKey: column.dateKey, columns: [column] });
      }
      return groups;
    }, []);
  });

  readonly visibleRows = computed(() => {
    const search = this.searchTerm().trim().toLocaleLowerCase();
    return this.users()
      .filter((user) => this.matchesSelectedTab(user))
      .map((user) => ({
        user,
        cells: this.columns().map((column) => this.getCell(user, column)),
      }))
      .filter((row) =>
        row.cells.some((cell) => cell.state === 'available' || cell.state === 'scheduled'),
      )
      .filter((row) => !search || this.displayName(row.user).toLocaleLowerCase().includes(search))
      .sort((left, right) =>
        this.displayName(left.user).localeCompare(this.displayName(right.user), 'nl', {
          sensitivity: 'base',
        }),
      );
  });

  constructor(public location: Location) {}

  ngOnInit() {
    this.subscribeUsers();
    this.subscribeDayparts();
    this.subscribeDaypartDays();
  }

  ngOnDestroy() {
    this.daypartDaysRequestId++;
    this.usersUnsubscribe?.();
    this.daypartsUnsubscribe?.();
    this.daypartDaysUnsubscribe?.();
  }

  subscribeUsers() {
    const relevantRoles = [Role.Driver];
    if (this.coDriverEnabled()) {
      relevantRoles.push(Role.CoDriver);
    }
    if (this.usesPlannerPlanning()) {
      relevantRoles.push(Role.Planner, Role.RidePlanner);
    }
    const usersQuery = query(
      collection(db, `organisations/${this.organisationId}/users`),
      where('roles', 'array-contains-any', relevantRoles),
      orderBy('name'),
    );

    this.usersUnsubscribe = onSnapshot(
      usersQuery,
      (snapshot) => {
        this.users.set(
          snapshot.docs.map(
            (userDoc) => ({ id: userDoc.id, ...userDoc.data() }) as User & { id: string },
          ),
        );
        this.markInitialSnapshot('users');
      },
      () => {
        this.error.set(true);
        this.loading.set(false);
      },
    );
  }

  subscribeDayparts() {
    this.daypartsUnsubscribe = onSnapshot(
      query(collection(db, `organisations/${this.organisationId}/dayparts`)),
      (snapshot) => {
        const dayparts = snapshot.docs
          .map((daypartDoc) => ({ ...daypartDoc.data(), id: daypartDoc.id }) as Daypart)
          .filter(
            (daypart) =>
              daypart.type === 'driver' ||
              (this.coDriverEnabled() && daypart.type === 'co-driver') ||
              (this.usesPlannerPlanning() && daypart.type === 'planner'),
          );
        this.dayparts.set(dayparts);
        this.markInitialSnapshot('dayparts');
      },
      () => {
        this.error.set(true);
        this.loading.set(false);
      },
    );
  }

  subscribeDaypartDays() {
    const requestId = ++this.daypartDaysRequestId;
    const selectedWeekStart = this.selectedWeekStart();
    const selectedWeekKey = this.weekString(selectedWeekStart);
    const cachedAssignments = this.assignmentCache.get(selectedWeekKey);
    this.daypartDaysUnsubscribe?.();
    if (cachedAssignments) {
      this.daypartDays.set(cachedAssignments);
      this.assignmentRefreshing.set(false);
    } else {
      this.daypartDays.set([]);
      this.assignmentRefreshing.set(true);
    }
    const daypartDaysQuery = query(
      collection(db, `organisations/${this.organisationId}/daypartDays`),
      where('week', '==', selectedWeekKey),
    );

    this.daypartDaysUnsubscribe = onSnapshot(
      daypartDaysQuery,
      (snapshot) => {
        if (requestId !== this.daypartDaysRequestId) return;
        const assignments = snapshot.docs.map(
          (daypartDay) => ({ ...daypartDay.data(), id: daypartDay.id }) as DaypartDay,
        );
        this.assignmentCache.set(selectedWeekKey, assignments);
        this.daypartDays.set(assignments);
        this.assignmentRefreshing.set(false);
        this.markInitialSnapshot('daypartDays');
      },
      () => {
        if (requestId !== this.daypartDaysRequestId) return;
        if (!cachedAssignments) {
          this.error.set(true);
          this.loading.set(false);
        }
        this.assignmentRefreshing.set(false);
      },
    );
    void this.preloadAdjacentWeeks(selectedWeekStart);
  }

  async preloadAdjacentWeeks(selectedWeekStart: Date) {
    const selectedWeek = moment(selectedWeekStart);
    const adjacentWeekKeys = [-7, 7].map((offset) =>
      this.weekString(selectedWeek.clone().add(offset, 'days').toDate()),
    );
    const missingWeekKeys = adjacentWeekKeys.filter(
      (weekKey) => !this.assignmentCache.has(weekKey),
    );
    if (missingWeekKeys.length === 0) return;

    const snapshot = await getDocs(
      query(
        collection(db, `organisations/${this.organisationId}/daypartDays`),
        where('week', 'in', missingWeekKeys),
      ),
    );
    const assignmentsByWeek = new Map<string, DaypartDay[]>();
    snapshot.docs.forEach((daypartDay) => {
      const assignment = { ...daypartDay.data(), id: daypartDay.id } as DaypartDay;
      const week = assignment.week;
      if (!week) return;
      assignmentsByWeek.set(week, [...(assignmentsByWeek.get(week) ?? []), assignment]);
    });
    missingWeekKeys.forEach((weekKey) => {
      this.assignmentCache.set(weekKey, assignmentsByWeek.get(weekKey) ?? []);
    });
  }

  setTab(index: number) {
    this.selectedIndex.set(index);
    this.tabTransitioning.set(true);
  }

  onTabAnimationDone() {
    this.tabTransitioning.set(false);
    this.refreshViewports();
  }

  refreshViewports() {
    afterNextRender(
      () => {
        this.viewports?.forEach((viewport) => {
          viewport.checkViewportSize();
          if (this.visibleRows().length > 0) {
            viewport.scrollToIndex(0);
          }
        });
      },
      { injector: this.injector },
    );
  }

  setSearchTerm(value: string) {
    this.searchTerm.set(value);
    this.refreshViewports();
  }

  clearSearch() {
    this.searchTerm.set('');
    this.refreshViewports();
  }

  changeWeek(direction: 'previous' | 'next') {
    const offset = direction === 'previous' ? -7 : 7;
    this.selectedWeekStart.set(moment(this.selectedWeekStart()).add(offset, 'days').toDate());
    this.subscribeDaypartDays();
  }

  selectWeek(date: Date | null) {
    if (!date) return;

    this.selectedWeekStart.set(moment(date).startOf('isoWeek').toDate());
    this.subscribeDaypartDays();
  }

  goToCurrentWeek() {
    const currentWeekStart = moment().startOf('isoWeek');
    if (currentWeekStart.isSame(this.selectedWeekStart(), 'day')) return;

    this.selectedWeekStart.set(currentWeekStart.toDate());
    this.subscribeDaypartDays();
  }

  syncHeaderScroll(header: HTMLElement, event: Event) {
    const source = event.target as HTMLElement;
    if (header.scrollLeft !== source.scrollLeft) {
      header.scrollLeft = source.scrollLeft;
    }
  }

  weekLabel(): string {
    const dates = this.weekDates();
    const start = dates[0];
    const end = dates[dates.length - 1];
    return `${moment(start).locale('nl').format('D MMM')} - ${moment(end)
      .locale('nl')
      .format('D MMM YYYY')}`;
  }

  dayLabel(date: Date): string {
    return moment(date).locale('nl').format('dddd D MMMM');
  }

  formatDaypartTime(daypart: Daypart): string {
    const { startHour, startMin, endHour, endMin } = daypart.daypart;
    return `${moment({ hour: startHour, minute: startMin }).format('HH:mm')} - ${moment({
      hour: endHour,
      minute: endMin,
    }).format('HH:mm')}`;
  }

  trackRow(_index: number, row: { user: User & { id: string } }): string {
    return row.user.id;
  }

  getCell(user: User & { id: string }, column: OverviewColumn): OverviewCell {
    const columnStart = moment(column.date).startOf('day').set({
      hour: column.daypart.daypart.startHour,
      minute: column.daypart.daypart.startMin,
      second: 0,
      millisecond: 0,
    });
    const columnEnd = moment(column.date).startOf('day').set({
      hour: column.daypart.daypart.endHour,
      minute: column.daypart.daypart.endMin,
      second: 0,
      millisecond: 0,
    });
    const hasException = (user.exceptions ?? []).some((exception) => {
      const exceptionStart = valueToDate(exception.dateFrom);
      const exceptionEnd = valueToDate(exception.dateTo);
      return (
        exceptionStart !== null &&
        exceptionEnd !== null &&
        exceptionStart < columnEnd.toDate() &&
        exceptionEnd > columnStart.toDate()
      );
    });
    if (hasException) {
      return { state: 'exception', ariaLabel: `${this.displayName(user)}: uitzondering` };
    }

    const assignments = this.daypartDays().filter(
      (daypartDay) =>
        daypartDay.daypartId === column.daypart.id &&
        this.daypartDayDateKey(daypartDay) === column.dateKey,
    );
    const indicators: string[] = [];
    if (assignments.some((assignment) => assignment.driverId === user.id)) indicators.push('C');
    if (assignments.some((assignment) => assignment.coDriverId === user.id)) indicators.push('B');
    if (assignments.some((assignment) => assignment.plannerIds?.includes(user.id))) {
      indicators.push('P');
    }
    if (indicators.length > 0) {
      return {
        state: 'scheduled',
        indicator: indicators.join('/'),
        ariaLabel: `${this.displayName(user)}: ingepland (${indicators.join('/')})`,
      };
    }

    const available = (column.daypart.availableUsers ?? []).some(
      (reference) => reference.id === user.id || reference.path?.endsWith(`/${user.id}`),
    );
    if (available) {
      return { state: 'available', ariaLabel: `${this.displayName(user)}: beschikbaar` };
    }

    return { state: 'unavailable', ariaLabel: `${this.displayName(user)}: niet beschikbaar` };
  }

  matchesSelectedTab(user: User & { id: string }): boolean {
    const roles = user.roles ?? [];
    if (this.selectedIndex() === 1) {
      return roles.includes(Role.Planner) || roles.includes(Role.RidePlanner);
    }
    return roles.includes(Role.Driver) || (this.coDriverEnabled() && roles.includes(Role.CoDriver));
  }

  displayName(user: User): string {
    return (
      `${user.name ?? ''} ${user.lastName ?? ''}`.trim() || user.email || user.id || 'Onbekend'
    );
  }

  compareDayparts(left: Daypart, right: Daypart): number {
    return (
      left.daypart.day - right.daypart.day ||
      left.daypart.startHour - right.daypart.startHour ||
      left.daypart.startMin - right.daypart.startMin ||
      left.daypart.endHour - right.daypart.endHour ||
      left.daypart.endMin - right.daypart.endMin
    );
  }

  weekString(date: Date): string {
    return `${moment(date).isoWeekYear()}-${moment(date).isoWeek()}`;
  }

  markInitialSnapshot(source: keyof typeof this.initialSnapshots) {
    this.initialSnapshots[source] = true;
    if (Object.values(this.initialSnapshots).every(Boolean)) {
      this.loading.set(false);
    }
  }

  dateKey(value: unknown): string {
    const parsedDate = valueToDate(value);
    if (!parsedDate) return '';

    return moment(parsedDate).format('YYYY-MM-DD');
  }

  daypartDayDateKey(daypartDay: DaypartDay): string {
    if (daypartDay.date) {
      const dateKey = this.dateKey(daypartDay.date);
      if (dateKey) return dateKey;
    }
    if (
      daypartDay.year !== undefined &&
      daypartDay.month !== undefined &&
      daypartDay.day !== undefined
    ) {
      // Keep date keys in ISO order so lexicographical comparisons remain chronological.
      return moment({
        year: daypartDay.year,
        month: daypartDay.month,
        day: daypartDay.day,
      }).format('YYYY-MM-DD');
    }
    return '';
  }
}
